/** Ask to keep a recording only when it is shorter than this. */
export const EMPTY_RECORDING_MAX_SECONDS = 30

/**
 * Final speech turns. Interim text and blank finals do not count — a
 * recording that never committed a line of speech is empty.
 */
export function countFinalSpeechSegments(segments: ReadonlyArray<{ isFinal: boolean; text: string }>): number {
  let count = 0
  for (const segment of segments) {
    if (segment.isFinal && segment.text.trim().length > 0) count += 1
  }
  return count
}

/**
 * Speech already stored on the meeting. `null` means the payload has no
 * segment list, so the caller cannot tell whether anyone spoke and must
 * not offer to delete. A saved line with no `isFinal` flag counts as final.
 */
export function countStoredSpeechSegments(meeting: unknown): number | null {
  if (typeof meeting !== "object" || meeting === null || !("streaming_segments" in meeting)) return null
  const raw = meeting.streaming_segments
  if (!Array.isArray(raw)) return null
  const segments: { isFinal: boolean; text: string }[] = []
  for (const item of raw) {
    if (typeof item !== "object" || item === null || !("text" in item) || typeof item.text !== "string") continue
    const isFinal = "isFinal" in item ? item.isFinal === true : true
    segments.push({ isFinal, text: item.text })
  }
  return countFinalSpeechSegments(segments)
}

/**
 * After a recording has already stopped and uploaded: ask to keep it only
 * when it is under 30 seconds and produced no final transcript segments.
 * Longer recordings, and any recording that captured speech, are kept
 * without a question.
 */
export function shouldAskToDiscardEmptyRecording(durationSeconds: number, finalSegmentCount: number): boolean {
  if (!Number.isFinite(durationSeconds) || durationSeconds < 0) return false
  if (durationSeconds >= EMPTY_RECORDING_MAX_SECONDS) return false
  if (!Number.isFinite(finalSegmentCount) || finalSegmentCount > 0) return false
  return true
}
